import { pickGenderText } from "../utils/genderText";

export const PHASE1_PROBLEM_KEY = "rel_breakup_fresh_pain_phase1";
export const PHASE1_DURATION_DAYS = 7;
export const PHASE1_REFERENCE_18_PROBLEM_KEY = "rel_breakup_fresh_pain_reference_18d";

export const PHASE1_STAGE_BY_DAY = {
  1: "pause",
  2: "pain",
  3: "pattern",
  4: "fear",
  5: "need",
  6: "softening",
  7: "continuation",
};

export const PHASE1_GATES = [
  {
    key: "relationship_pain",
    labels: { he: "כאב בקשר", en: "relationship pain", es: "dolor en la relacion" },
  },
  {
    key: "longing",
    labels: { he: "געגוע", en: "longing", es: "anhelo" },
  },
  {
    key: "fear_of_losing",
    labels: { he: "פחד לאבד", en: "fear of losing", es: "miedo a perder" },
  },
  {
    key: "self_criticism",
    labels: { he: "ביקורת עצמית", en: "self-criticism", es: "autocritica" },
  },
  {
    key: "loneliness",
    labels: { he: "בדידות", en: "loneliness", es: "soledad" },
  },
  {
    key: "other",
    labels: { he: "משהו אחר", en: "something else", es: "algo mas" },
  },
];

const ECHO_BY_STAGE = {
  pause: {
    he: "עצרת רגע עם עצמך.",
    en: "You paused for a moment with yourself.",
    es: "Te detuviste un momento contigo.",
  },
  pain: {
    he: "פגשת רגע של כאב.",
    en: "You met a moment of pain.",
    es: "Te encontraste con un momento de dolor.",
  },
  pattern: {
    he: "שמת לב למשהו שקורה בך.",
    en: "You noticed something that keeps happening in you.",
    es: "Notaste algo que sigue pasando en ti.",
  },
  fear: {
    he: "נגעת בפחד שהיה שם.",
    en: "You touched the fear that was there.",
    es: "Tocaste el miedo que estaba ahi.",
  },
  need: {
    he: "משהו עמוק בך ביקש מקום.",
    en: "Something deep in you asked for space.",
    es: "Algo profundo en ti pidio espacio.",
  },
  softening: {
    he: "משהו בך התרכך קצת.",
    en: "Something in you softened a little.",
    es: "Algo en ti se suavizo un poco.",
  },
  continuation: {
    he: "נשארת עם עצמך עוד רגע.",
    en: "You stayed with yourself a little longer.",
    es: "Te quedaste contigo un momento mas.",
  },
  inner_world: {
    he: "פגשת חלק עמוק יותר בך.",
    en: "You met a deeper part of yourself.",
    es: "Te encontraste con una parte mas profunda de ti.",
  },
  perspective: {
    he: "משהו בתמונה התחיל להסתדר אחרת.",
    en: "Something in the picture began to arrange itself differently.",
    es: "Algo en la imagen empezo a ordenarse de otra manera.",
  },
  strength: {
    he: "משהו בך נשאר איתך קצת יותר.",
    en: "Something in you stayed with you a little more.",
    es: "Algo en ti se quedo contigo un poco mas.",
  },
};

export function isPhaseOneProblemKey(problemKey) {
  const safeKey = String(problemKey || PHASE1_PROBLEM_KEY).trim();
  return safeKey === PHASE1_PROBLEM_KEY || safeKey === PHASE1_REFERENCE_18_PROBLEM_KEY;
}

/** שער "משהו אחר" — לא נושא סופי; המשתמש חייב לבחור מסע אמיתי. */
export const PHASE1_GATE_KEY_OTHER = "other";

export function isPhaseOnePlaceholderGateKey(gateKey) {
  return String(gateKey || "").trim() === PHASE1_GATE_KEY_OTHER;
}

/** אינטייק שמזוהה כבחירת־other בלבד (כרגיל או לפני שדה entryGateKey הוכנס). */
export function isPhaseOneOtherIntakeSnapshot(intake, lang = "he") {
  if (!intake || typeof intake !== "object") return false;
  if (isPhaseOnePlaceholderGateKey(intake.entryGateKey)) return true;
  const gate = PHASE1_GATES.find((g) => g.key === PHASE1_GATE_KEY_OTHER);
  if (!gate?.labels) return false;
  const pain = String(intake.painNow || "").trim();
  if (!pain) return false;
  return Object.values(gate.labels).some((label) => String(label || "").trim() === pain);
}

export function getPhaseOneProblemKey() {
  return PHASE1_PROBLEM_KEY;
}

export function getPhaseOneStage(dayNumber) {
  return PHASE1_STAGE_BY_DAY[Number(dayNumber)] || "pause";
}

export function getPhaseOneEcho(stage, lang = "he") {
  const safeLang = String(lang || "he").toLowerCase();
  const node = ECHO_BY_STAGE[stage] || ECHO_BY_STAGE.pause;
  return node[safeLang] || node.he;
}

export function getPhaseOneGateLabel(gateKey, lang = "he") {
  const safeLang = String(lang || "he").toLowerCase();
  const gate = PHASE1_GATES.find((item) => item.key === gateKey) || PHASE1_GATES[0];
  return gate.labels[safeLang] || gate.labels.he;
}

export function getPhaseOneEntryQuestion(lang = "he", gender = "other") {
  const safeLang = String(lang || "he").toLowerCase();
  if (safeLang === "en") return "What are you sitting with right now?";
  if (safeLang === "es") return "Con que estas sentado ahora mismo?";
  return pickGenderText(
    {
      male: "עם מה אתה יושב עכשיו?",
      female: "עם מה את יושבת עכשיו?",
      other: "עם מה אתה יושב עכשיו?",
    },
    gender
  );
}

export function getPhaseOneReadyButton(lang = "he", _gender = "other") {
  const safeLang = String(lang || "he").toLowerCase();
  if (safeLang === "en") return "Begin";
  if (safeLang === "es") return "Empezar";
  return "להתחיל";
}

export function getPhaseOneGenderPrompt(lang = "he") {
  const safeLang = String(lang || "he").toLowerCase();
  if (safeLang === "en") return "I'll use the form of address that fits you best.";
  if (safeLang === "es") return "Usaré la forma de tratamiento que mejor te encaje.";
  return "אני אשתמש בלשון שהכי נכונה לך.";
}

export function getPhaseOneReturningPrompt(lang = "he") {
  const safeLang = String(lang || "he").toLowerCase();
  if (safeLang === "en") return "Do you want to pause again?";
  if (safeLang === "es") return "Quieres detenerte otra vez?";
  return "רוצה לעצור רגע שוב?";
}

export function getPhaseOneHereButton(lang = "he") {
  const safeLang = String(lang || "he").toLowerCase();
  if (safeLang === "en") return "I am here";
  if (safeLang === "es") return "Estoy aqui";
  return "אני כאן";
}
